import type { FilterOptionsState } from '@mui/material';
import MuiAutocomplete from '@mui/material/Autocomplete';
import { deepEqual, useTranslate } from '@si/utils';
import { useId } from 'react';

import { TextInput, type TextInputProps } from '../Input/TextInput';

type Props<T> = Omit<TextInputProps, 'onChange' | 'value' | 'endAdornment'> & {
  defaultValue?: T;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  getOptionLabel: (option: T) => string;
  onChange: (value: T | null) => void;
  onInputChange?: (value: string) => void;
  options: T[];
  value?: T | null;
  popperHidden?: boolean;
};

export function Autocomplete<T>({
  defaultValue,
  errorText,
  filterOptions,
  getOptionLabel,
  helperText,
  id,
  inputWidth = '100%',
  label,
  onChange,
  onInputChange = () => {},
  options,
  overrideVisited = false,
  required = false,
  startAdornment,
  value,
  popperHidden = false,
}: Props<T>) {
  const { t, translate } = useTranslate();
  const generatedId = useId();
  const autocompleteId = id ?? generatedId;
  const maxWidth = inputWidth === '10rem' || inputWidth === '15rem' ? '20rem' : inputWidth;

  return (
    <MuiAutocomplete
      slotProps={{
        popper: { hidden: popperHidden },
      }}
      defaultValue={defaultValue}
      filterOptions={filterOptions}
      fullWidth
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => deepEqual(option, value)}
      noOptionsText={translate(t.noResults)}
      onChange={(_, value) => onChange(value)}
      onInputChange={(_, newInputValue) => onInputChange(newInputValue)}
      options={options}
      sx={{
        maxWidth,
      }}
      value={value}
      renderInput={(params) => (
        <TextInput
          {...params}
          errorText={errorText}
          helperText={helperText}
          id={autocompleteId}
          inputWidth={inputWidth}
          label={label}
          onChange={params.inputProps.onChange}
          overrideVisited={overrideVisited}
          required={required}
          startAdornment={startAdornment}
          value={params.inputProps.value?.toString() ?? ''}
          slotProps={{
            input: {
              ...params.InputProps,
              sx: { py: '0!important' },
            },
            htmlInput: { ...params.inputProps, id: autocompleteId, name: autocompleteId },
          }}
        />
      )}
    />
  );
}
